import { useSyncExternalStore } from 'react'

/**
 * Operação que não pode ser interrompida: cobrança no TEF, registro do pagamento,
 * envio do pedido, espera da senha e da nota.
 *
 * Existe porque o vigia de inatividade vive fora da árvore de quem paga (ele é
 * montado na raiz do totem) e não tem como saber que a maquininha está com o
 * cliente. Sem isso o totem zera o carrinho e volta ao início no meio de uma
 * cobrança — no Android nem o aviso aparece, porque a tela da Elgin cobre a do
 * totem. Também segura o reload do PWA e o auto-update do Electron.
 *
 * Teto: passado {@link TETO_PAUSA_MS} sem o terminal responder, quem acompanha
 * pode voltar a agir (ver `haOperacaoCritica` e `desdeOperacaoCritica`) — mas a
 * tela só deve avisar, nunca reiniciar enquanto houver chamada pendente.
 */

/** Alinhado ao teto do app Android para uma transação TEF. */
export const TETO_PAUSA_MS = 5 * 60_000

let emAndamento = 0
let inicio: number | null = null
const inscritos = new Set<() => void>()

function avisar(): void {
  for (const cb of inscritos) cb()
}

/** Abre uma operação crítica. Devolve o encerrador, idempotente. */
export function iniciarOperacaoCritica(): () => void {
  emAndamento += 1
  if (emAndamento === 1) inicio = Date.now()
  avisar()

  let encerrado = false
  return () => {
    if (encerrado) return
    encerrado = true
    emAndamento = Math.max(0, emAndamento - 1)
    if (emAndamento === 0) inicio = null
    avisar()
  }
}

export function haOperacaoCritica(): boolean {
  return emAndamento > 0
}

/** Há quanto tempo (ms) a operação mais antiga está aberta, ou `null`. */
export function desdeOperacaoCritica(): number | null {
  return inicio == null ? null : Date.now() - inicio
}

/** Passou do teto: a tela pode avisar, mas não reiniciar sozinha. */
export function passouDoTeto(): boolean {
  const desde = desdeOperacaoCritica()
  return desde != null && desde > TETO_PAUSA_MS
}

export function assinarOperacaoCritica(cb: () => void): () => void {
  inscritos.add(cb)
  return () => inscritos.delete(cb)
}

/** Para componentes: re-renderiza quando abre ou fecha uma operação crítica. */
export function useOperacaoCritica(): boolean {
  return useSyncExternalStore(assinarOperacaoCritica, haOperacaoCritica, () => false)
}

/** Só para teste: zera o estado do módulo entre casos. */
export function resetarOperacaoCriticaParaTeste(): void {
  emAndamento = 0
  inicio = null
  avisar()
}
